/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {
  alpha,
  Box,
  ToggleButtonGroup as MuiToggleButtonGroup,
  type SxProps,
  type Theme,
  type ToggleButtonGroupProps,
} from '@mui/material';
import { type Ref, useLayoutEffect, useRef, useState } from 'react';
import { toggleButtonBorderRadius } from '../../styles';

interface Indicator {
  left: number;
  top: number;
  width: number;
  height: number;
  /** The first placement appears in place; later ones slide. */
  animate: boolean;
}

/**
 * MUI's ToggleButtonGroup whose selected highlight slides to the picked option instead of
 * appearing there. Exclusive groups only; multi-select groups keep a highlight per option.
 * Drop-in: same props as MUI's.
 */
export default function ToggleButtonGroup({
  children,
  className,
  color,
  ref,
  sx,
  ...props
}: Readonly<Omit<ToggleButtonGroupProps, 'ref'> & { ref?: Ref<HTMLDivElement> }>) {
  const groupRef = useRef<HTMLDivElement | null>(null);
  const [indicator, setIndicator] = useState<Indicator | null>(null);
  const animated = Boolean(props.exclusive);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the selection or the options change.
  useLayoutEffect(() => {
    const group = groupRef.current;
    if (!group || !animated) return;
    const measure = () => {
      const selected = group.querySelector<HTMLElement>('.MuiToggleButton-root.Mui-selected');
      if (!selected) {
        setIndicator(null);
        return;
      }
      const groupBox = group.getBoundingClientRect();
      const box = selected.getBoundingClientRect();
      setIndicator((current) => ({
        left: box.left - groupBox.left - group.clientLeft,
        top: box.top - groupBox.top - group.clientTop,
        width: box.width,
        height: box.height,
        animate: current !== null,
      }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(group);
    return () => observer.disconnect();
  }, [animated, props.value, children]);

  return (
    <MuiToggleButtonGroup
      {...props}
      color={color}
      ref={(instance) => {
        const node = instance as HTMLDivElement | null;
        groupRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      className={[className, animated && 'pa-toggle-animated'].filter(Boolean).join(' ')}
      sx={
        [
          { position: 'relative', isolation: 'isolate' },
          ...(Array.isArray(sx) ? sx : [sx]),
        ] as SxProps<Theme>
      }
    >
      {indicator && (
        <Box
          aria-hidden
          className="pa-toggle-indicator"
          sx={(theme) => {
            const palette =
              color && color !== 'standard' ? theme.palette[color] : theme.palette.primary;
            return {
              position: 'absolute',
              zIndex: 0,
              left: 0,
              top: 0,
              width: indicator.width,
              height: indicator.height,
              transform: `translate(${indicator.left}px, ${indicator.top}px)`,
              borderRadius: `${toggleButtonBorderRadius - 3}px`,
              backgroundColor: alpha(palette.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
              pointerEvents: 'none',
              transition: indicator.animate
                ? theme.transitions.create(['transform', 'width', 'height'], { duration: 220 })
                : 'none',
            };
          }}
        />
      )}
      {children}
    </MuiToggleButtonGroup>
  );
}
